import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { BookOpen, Code, Download, ExternalLink } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import SEO, { createHowToSchema } from "@/components/SEO";

const Documentation = () => {
  const navigate = useNavigate();
  
  const handleApiReferenceClick = () => {
    toast.info('API Reference is in development. Please contact our sales team for more updates and early access.');
  };

  const handleReadGuideClick = () => {
    navigate('/blog');
  };

  // HowTo schema for documentation
  const howToSchema = createHowToSchema(
    [
      { name: 'Create Account', text: 'Sign up for a free account to get started with AI image generation' },
      { name: 'Enter Prompts', text: 'Enter your image prompts or upload a CSV file with multiple prompts' },
      { name: 'Select Model', text: 'Choose from FLUX.1 Schnell, Pro, or other available AI models' },
      { name: 'Generate Images', text: 'Click generate and watch your AI images appear in real-time' },
      { name: 'Download', text: 'Download individual images or batch download as a ZIP file' },
    ],
    'How to Generate Bulk AI Images',
    'Step-by-step guide to generating thousands of AI images using The Bulk Image Generator',
    'PT10M'
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <SEO
        title="API Documentation | Developer Guide | The Bulk Image Generator"
        description="Access comprehensive API documentation for The Bulk Image Generator. Integrate AI image generation into your applications with our developer-friendly guides and SDKs."
        keywords="bulk image generator api, ai image api documentation, developer guide, integration, sdk, image generation api, rest api"
        url="/docs"
        jsonLd={howToSchema}
      />
      <Header />
      
      <main className="pt-20">
        <div className="container mx-auto px-6 py-12">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-gradient-to-r from-blue-600/20 to-purple-600/20 text-blue-300 border-blue-500/30">
              Documentation
            </Badge>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">
              Developer <span className="bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">Documentation</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Everything you need to integrate and use The Bulk Image Generator
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all duration-300">
              <CardHeader>
                <BookOpen className="w-8 h-8 text-blue-400 mb-4" />
                <CardTitle className="text-white">Getting Started</CardTitle>
              </CardHeader>
              <CardContent className="text-gray-300">
                <p className="mb-4">Quick start guide to begin generating images with our platform.</p>
                <Button 
                  onClick={handleReadGuideClick}
                  variant="outline" 
                  className="text-blue-400 border-blue-400 bg-cyan-50"
                >
                  Read Guide <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all duration-300">
              <CardHeader>
                <Code className="w-8 h-8 text-purple-400 mb-4" />
                <CardTitle className="text-white">API Reference</CardTitle>
              </CardHeader>
              <CardContent className="text-gray-300">
                <p className="mb-4">Complete API documentation with examples and code samples.</p>
                <Button 
                  onClick={handleApiReferenceClick}
                  variant="outline" 
                  className="text-gray-400 border-gray-400 bg-gray-50 cursor-not-allowed opacity-75"
                  disabled
                >
                  Coming Soon <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>

            <Card className="bg-white/5 backdrop-blur-md border-white/10 hover:bg-white/10 transition-all duration-300">
              <CardHeader>
                <Download className="w-8 h-8 text-green-400 mb-4" />
                <CardTitle className="text-white">SDKs & Libraries</CardTitle>
              </CardHeader>
              <CardContent className="text-gray-300">
                <p className="mb-4">Official SDKs for popular programming languages.</p>
                <Button 
                  variant="outline" 
                  className="text-gray-400 border-gray-400 bg-gray-50 cursor-not-allowed opacity-75"
                  disabled
                >
                  Coming Soon <ExternalLink className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default Documentation;
